// src/App.tsx
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { lazy, Suspense, useCallback } from "react";
import lightTheme from "./theme";
import { useUser } from "./hooks/useUser";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import { PageLoadingSpinner } from "./components/loading";
import LoginPage from "./pages/Login/LoginPage";
import FaqPage from "./pages/Faq/FaqPage";
import SessionExpiryWarning from "./components/SessionExpiryWarning";

const DashboardLayout = lazy(() => import("./components/layout/DashboardLayout"));
const DashboardPage = lazy(() => import("./pages/Dashboard/DashboardPage"));
const CommitmentPortfolioPage = lazy(
  () => import("./pages/CommitmentPortfolio/CommitmentPortfolioPage")
);
const ActionsPage = lazy(() => import("./pages/Actions/ActionsPage"));
const NotificationsPage = lazy(
  () => import("./pages/Notifications/NotificationsPage")
);
const BulkCommitmentsDashboard = lazy(
  () => import("./pages/BulkCommitments/BulkCommitmentsDashboard")
);
const BulkCommitmentsParsePage = lazy(
  () => import("./pages/BulkCommitments/BulkCommitmentsParsePage")
);
const ProfilePage = lazy(() => import("./pages/Profile/ProfilePage"));
const ClassicLayout = lazy(() => import("./pages/Classic/ClassicLayout"));
const ClassicActionsPage = lazy(
  () => import("./pages/Classic/ClassicActionsPage")
);
const ClassicOldUIPortfolioReplica = lazy(
  () => import("./pages/Classic/ClassicOldUIPortfolioReplica")
);
const VibeLayout = lazy(() => import("./pages/Vibe/VibeLayout"));
const VibeDashboardPage = lazy(() => import("./pages/Vibe/VibeDashboardPage"));
const VibePortfolioPage = lazy(() => import("./pages/Vibe/VibePortfolioPage"));
const VibeActionsPage = lazy(() => import("./pages/Vibe/VibeActionsPage"));
const VibeNotificationsPage = lazy(
  () => import("./pages/Vibe/VibeNotificationsPage")
);
const VibeBulkDashboardPage = lazy(
  () => import("./pages/Vibe/VibeBulkDashboardPage")
);
const VibeBulkParsePage = lazy(() => import("./pages/Vibe/VibeBulkParsePage"));
const VibeProfilePage = lazy(() => import("./pages/Vibe/VibeProfilePage"));
const VibeFaqPage = lazy(() => import("./pages/Vibe/VibeFaqPage"));

function App() {
  const { isAuthenticated } = useUser();
  const Router = import.meta.env.VITE_DEMO_FILE_MODE === "true" ? HashRouter : BrowserRouter;

  const loadAccount = useCallback(async (): Promise<void> => {
    // User authentication is now handled by User Context
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <Router>
          <SessionExpiryWarning />
          <Suspense fallback={<PageLoadingSpinner minHeight={320} />}>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route
                path="/login"
                element={
                  <PublicRoute isAuthenticated={isAuthenticated}>
                    <LoginPage locale="en-US" loadAccount={loadAccount} />
                  </PublicRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <DashboardLayout>
                      <DashboardPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/CommitmentPortfolio"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <DashboardLayout>
                      <CommitmentPortfolioPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/Actions"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <DashboardLayout>
                      <ActionsPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/actions"
                element={<Navigate to="/dashboard/Actions" replace />}
              />

              <Route
                path="/dashboard/Notifications"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <DashboardLayout>
                      <NotificationsPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/BulkCommitments"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <DashboardLayout>
                      <BulkCommitmentsDashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/BulkCommitments/parse"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <DashboardLayout>
                      <BulkCommitmentsParsePage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/Profile"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/Faq"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <DashboardLayout>
                      <FaqPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vibe"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <VibeLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="actions" replace />} />
                <Route path="dashboard" element={<VibeDashboardPage />} />
                <Route
                  path="commitment-portfolio"
                  element={<VibePortfolioPage />}
                />
                <Route path="actions" element={<VibeActionsPage />} />
                <Route
                  path="notifications"
                  element={<VibeNotificationsPage />}
                />
                <Route path="bulk" element={<VibeBulkDashboardPage />} />
                <Route path="bulk/parse" element={<VibeBulkParsePage />} />
                <Route path="profile" element={<VibeProfilePage />} />
                <Route path="faq" element={<VibeFaqPage />} />
              </Route>
              <Route
                path="/classic"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <ClassicLayout />
                  </ProtectedRoute>
                }
              >
                <Route
                  index
                  element={<Navigate to="commitment-portfolio" replace />}
                />
                <Route
                  path="commitment-portfolio"
                  element={<ClassicOldUIPortfolioReplica />}
                />
                <Route path="actions" element={<ClassicActionsPage />} />
                <Route path="faq" element={<FaqPage />} />
              </Route>
            </Routes>
          </Suspense>
        </Router>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default App;
